/**
 * A fetch whose answer must start within `limitMs`, or it fails with a TimeoutError (shown as a
 * connection problem). Only the wait for the answer is limited, not reading its body, so a large
 * download that is already arriving is never cut off. A caller's own signal still cancels it.
 */
export function answerWithin(limitMs: number, fetchImpl: typeof fetch = (...args) => fetch(...args)): typeof fetch {
  return async (input, init = {}) => {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(new DOMException('no answer in time', 'TimeoutError')), limitMs);
    const outer = init.signal;
    const forward = () => abort.abort(outer?.reason);
    if (outer?.aborted) forward(); else outer?.addEventListener('abort', forward, { once: true });
    try { return await fetchImpl(input, { ...init, signal: abort.signal }); }
    finally { clearTimeout(timer); outer?.removeEventListener('abort', forward); }
  };
}

/** Time to send `bytes` at a slow but working 50 kB/s, plus 30 s: longer means the upload stalled. */
export const uploadLimitMs = (bytes: number) => 30_000 + Math.ceil(bytes / 50);
